import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassPanel, Icon } from '../components/common'
import { BRANDING } from '../config/branding'
import { usePrices } from '../hooks/usePrices'
import { formatPrice } from '../config/pricing'
import './PaymentView.css'

// ============================================
// Animation Variants
// ============================================

const pageVariants = {
  initial: { opacity: 0, y: 20, filter: 'blur(4px)' },
  animate: { 
    opacity: 1, 
    y: 0, 
    filter: 'blur(0px)',
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
  },
  exit: { opacity: 0, y: -20, filter: 'blur(4px)', transition: { duration: 0.2 } }
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] }
  }
}

const strategyVariants = {
  initial: { opacity: 0, x: 50 },
  animate: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 0.3, ease: 'easeOut' }
  },
  exit: { 
    opacity: 0, 
    x: -50,
    transition: { duration: 0.2 }
  }
}

// ============================================
// Approved Copy (from Master Plan Section 7)
// ============================================

const COPY = {
  subscription: {
    headline: 'Your ongoing story universe',
    subhead: 'Everything you need to build, expand, and maintain your story over time.',
    features: [
      'Unlimited evolving projects',
      'Characters, timelines, locations, and events that stay connected',
      'Continuous access to new tools and features',
      'Collaborative by default',
      'Always up to date',
    ],
    footer: 'Best for creators building long-term worlds or multiple projects.',
  },
  project: {
    headline: 'One project. One payment. Yours.',
    subhead: 'Perfect for focused projects you want to build, finish, and keep.',
    coreCopy: 'Create a complete story world with full access, no subscription required.',
    features: [
      'Full access for this project',
      'Unlimited edits',
      'Export anytime',
      'No recurring payments',
      'Optional upgrades later',
    ],
    footer: 'Ideal for films, scripts, books, or one-off creative projects.',
  },
  lifetime: {
    headline: 'Lifetime access. One payment.',
    subhead: 'Pay once and unlock everything—now and in the future.',
    features: [
      'All current and future features',
      'Unlimited projects and storage',
      'Priority support',
      'API access and custom branding',
      'Founder badge and early access',
    ],
  },
  page: {
    title: 'Choose your plan',
    subtitle: 'Find the perfect fit for your creative workflow',
  },
  footer: {
    secure: 'Secure Payment',
    cards: 'All Major Cards',
    global: 'Available Worldwide',
    note: 'All prices in USD. Prices may vary by region.',
  },
}

// ============================================
// FAQ Data
// ============================================

const FAQ_DATA = {
  subscription: [
    {
      q: 'Can I switch between monthly and yearly?',
      a: 'Yes! You can upgrade to yearly at any time. We\'ll prorate your remaining monthly balance as credit toward your yearly plan.',
    },
    {
      q: 'What happens if I cancel?',
      a: 'You\'ll retain access until the end of your billing period. Your projects and data remain accessible in read-only mode.',
    },
    {
      q: 'Are there any limits on projects?',
      a: 'No limits! Both plans include unlimited projects, team members, and storage.',
    },
  ],
  project: [
    {
      q: 'Do I need to pay again for updates?',
      a: 'No, all future updates to your purchased projects are free. You only pay once per project.',
    },
    {
      q: 'Can I transfer a project to someone else?',
      a: 'Yes, project ownership can be transferred to any user with an account.',
    },
    {
      q: 'Can I upgrade to a subscription later?',
      a: 'Absolutely! You can upgrade to a subscription at any time and your project purchase will be credited.',
    },
  ],
  lifetime: [
    {
      q: 'Is this really lifetime access?',
      a: 'Yes! One payment gives you permanent access to all current and future features as long as the platform exists.',
    },
    {
      q: 'What if the price changes later?',
      a: 'Your purchase is locked in at today\'s price. Future price changes won\'t affect your access.',
    },
    {
      q: 'Is there a refund policy?',
      a: 'Yes, we offer a 14-day money-back guarantee, no questions asked.',
    },
  ],
}

// ============================================
// PlanSelector Component
// ============================================

function PlanSelector({ activeTab, onChange }) {
  return (
    <div className="payment-view__plan-selector">
      {/* Binary Toggle for Subscription/Project */}
      <div className="payment-view__toggle-group">
        <button
          className={`payment-view__toggle-option ${activeTab === 'subscription' ? 'payment-view__toggle-option--active' : ''}`}
          onClick={() => onChange('subscription')}
        >
          <Icon name="timeline" size={18} />
          <span>Subscription</span>
          {activeTab === 'subscription' && (
            <motion.div
              className="payment-view__toggle-indicator"
              layoutId="toggleIndicator"
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            />
          )}
        </button>
        <button
          className={`payment-view__toggle-option ${activeTab === 'project' ? 'payment-view__toggle-option--active' : ''}`}
          onClick={() => onChange('project')}
        >
          <Icon name="projects" size={18} />
          <span>Pay Per Project</span>
          {activeTab === 'project' && (
            <motion.div
              className="payment-view__toggle-indicator"
              layoutId="toggleIndicator"
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            />
          )}
        </button>
      </div>

      {/* Separator */}
      <div className="payment-view__selector-divider" />

      {/* Lifetime Special Tab */}
      <button
        className={`payment-view__lifetime-tab ${activeTab === 'lifetime' ? 'payment-view__lifetime-tab--active' : ''}`}
        onClick={() => onChange('lifetime')}
      >
        <Icon name="lifetime" size={18} />
        <span>Lifetime</span>
      </button>
    </div>
  )
}

// ============================================
// PricingCard Component
// ============================================

function PricingCard({ 
  title, 
  price, 
  period, 
  features, 
  popular, 
  buttonText = 'Get Started', 
  badge,
  savings,
  onClick,
  loading,
}) {
  return (
    <motion.div
      className={`payment-view__pricing-card ${popular ? 'payment-view__pricing-card--popular' : ''}`}
      variants={itemVariants}
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
    >
      {popular && (
        <div className="payment-view__popular-badge">
          <Icon name="star" size={12} /> Most Popular
        </div>
      )}
      {badge && (
        <div className="payment-view__card-badge">{badge}</div>
      )}
      
      <div className="payment-view__card-header">
        <h3 className="payment-view__card-title">{title}</h3>
        <div className="payment-view__price-block">
          {loading ? (
            <span className="payment-view__price-skeleton" />
          ) : price !== null && price !== undefined ? (
            <>
              <span className="payment-view__price">{formatPrice(price)}</span>
              {period && <span className="payment-view__period">/{period}</span>}
            </>
          ) : (
            <span className="payment-view__price-hidden" />
          )}
        </div>
        {savings && (
          <div className="payment-view__savings">{savings}</div>
        )}
      </div>

      <ul className="payment-view__features-list">
        {features.map((feature, i) => (
          <li key={i} className="payment-view__feature-item">
            <span className="payment-view__feature-check">
              <Icon name="check" size={12} />
            </span>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <button 
        className={`payment-view__cta-btn ${popular ? 'payment-view__cta-btn--primary' : ''}`}
        onClick={onClick}
      >
        {buttonText}
      </button>
    </motion.div>
  )
}

// ============================================
// BillingToggle Component
// ============================================

function BillingToggle({ isYearly, onChange }) {
  return (
    <div className="payment-view__billing-toggle">
      <span className={!isYearly ? 'payment-view__toggle-label--active' : ''}>Monthly</span>
      <button
        className={`payment-view__toggle-switch ${isYearly ? 'payment-view__toggle-switch--yearly' : ''}`}
        onClick={() => onChange(!isYearly)}
        aria-label="Toggle billing period"
      >
        <motion.div
          className="payment-view__toggle-knob"
          layout
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
      </button>
      <span className={isYearly ? 'payment-view__toggle-label--active' : ''}>
        Yearly <span className="payment-view__save-badge">Save 27%</span>
      </span>
    </div>
  )
}

// ============================================
// FAQSection Component
// ============================================

function FAQSection({ faqs }) {
  const [openIndex, setOpenIndex] = useState(null)

  return (
    <div className="payment-view__faq">
      <h3 className="payment-view__faq-title">
        <Icon name="faq" size={20} />
        Frequently Asked Questions
      </h3>
      
      <div className="payment-view__faq-list">
        {faqs.map((faq, index) => (
          <motion.div
            key={index}
            className={`payment-view__faq-item ${openIndex === index ? 'payment-view__faq-item--open' : ''}`}
            layout
          >
            <button
              className="payment-view__faq-question"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              <span>{faq.q}</span>
              <span className="payment-view__faq-toggle">
                {openIndex === index ? '−' : '+'}
              </span>
            </button>
            <AnimatePresence>
              {openIndex === index && (
                <motion.div
                  className="payment-view__faq-answer"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <p>{faq.a}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

// ============================================
// ValueComparison Component
// ============================================

function ValueComparison({ prices, loading }) {
  // Only show if we have prices
  if (loading || !prices?.subscription?.yearly || !prices?.lifetime?.standard) {
    return null
  }

  const yearlyPrice = prices.subscription.yearly
  const lifetimePrice = prices.lifetime.standard
  const monthlyEquivalent = Math.round(lifetimePrice / 24 / 100)
  const yearsToBreakEven = (lifetimePrice / yearlyPrice).toFixed(1)

  return (
    <div className="payment-view__value-comparison">
      <div className="payment-view__comparison-header">
        <Icon name="value" size={18} />
        <span>Value Breakdown</span>
      </div>
      
      <div className="payment-view__comparison-grid">
        <div className="payment-view__comparison-item">
          <div className="payment-view__comparison-value">~${monthlyEquivalent}</div>
          <div className="payment-view__comparison-label">per month (2 years)</div>
        </div>
        <div className="payment-view__comparison-item">
          <div className="payment-view__comparison-value">{yearsToBreakEven}yr</div>
          <div className="payment-view__comparison-label">to break even vs yearly</div>
        </div>
        <div className="payment-view__comparison-item">
          <div className="payment-view__comparison-value">∞</div>
          <div className="payment-view__comparison-label">savings after year 3</div>
        </div>
      </div>
    </div>
  )
}

// ============================================
// Strategy Screens
// ============================================

function SubscriptionStrategy({ prices, loading }) {
  const [isYearly, setIsYearly] = useState(true)

  // Get subscription features based on billing period
  const yearlyFeatures = [
    ...COPY.subscription.features,
    'Save with annual billing',
  ]

  return (
    <motion.div
      className="payment-view__strategy-content"
      variants={strategyVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className="payment-view__strategy-intro">
        <h2>{COPY.subscription.headline}</h2>
        <p>{COPY.subscription.subhead}</p>
      </div>

      <BillingToggle isYearly={isYearly} onChange={setIsYearly} />

      <motion.div 
        className="payment-view__cards-grid payment-view__cards-grid--two"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <PricingCard
          title="Monthly"
          price={prices?.subscription?.monthly}
          period="month"
          features={COPY.subscription.features}
          buttonText="Start Monthly"
          popular={!isYearly}
          loading={loading}
        />
        <PricingCard
          title="Yearly"
          price={prices?.subscription?.yearly}
          period="year"
          features={yearlyFeatures}
          buttonText="Start Yearly"
          popular={isYearly}
          savings="Best value"
          loading={loading}
        />
      </motion.div>

      <div className="payment-view__strategy-footer">
        <p>{COPY.subscription.footer}</p>
      </div>

      <FAQSection faqs={FAQ_DATA.subscription} />
    </motion.div>
  )
}

function ProjectStrategy({ prices, loading }) {
  return (
    <motion.div
      className="payment-view__strategy-content"
      variants={strategyVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className="payment-view__strategy-intro">
        <h2>{COPY.project.headline}</h2>
        <p>{COPY.project.subhead}</p>
      </div>

      <div className="payment-view__project-explainer">
        <p>{COPY.project.coreCopy}</p>
      </div>

      <motion.div 
        className="payment-view__cards-grid payment-view__cards-grid--single"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <PricingCard
          title="Single Project"
          price={prices?.project?.single}
          features={COPY.project.features}
          buttonText="Purchase This Project"
          popular
          loading={loading}
        />
      </motion.div>

      <div className="payment-view__strategy-footer">
        <p>{COPY.project.footer}</p>
        <button className="payment-view__compare-link">
          Compare subscription <Icon name="arrow-right" size={14} />
        </button>
      </div>

      <FAQSection faqs={FAQ_DATA.project} />
    </motion.div>
  )
}

function LifetimeStrategy({ prices, loading }) {
  return (
    <motion.div
      className="payment-view__strategy-content"
      variants={strategyVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className="payment-view__strategy-intro payment-view__strategy-intro--lifetime">
        <h2>{COPY.lifetime.headline}</h2>
        <p>{COPY.lifetime.subhead}</p>
      </div>

      <div className="payment-view__lifetime-layout">
        <motion.div 
          className="payment-view__lifetime-card-wrapper"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            className="payment-view__pricing-card payment-view__pricing-card--lifetime"
            variants={itemVariants}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
          >
            <div className="payment-view__lifetime-glow" />
            
            <div className="payment-view__card-header">
              <h3 className="payment-view__card-title">Lifetime Access</h3>
              <div className="payment-view__price-block">
                {loading ? (
                  <span className="payment-view__price-skeleton" />
                ) : prices?.lifetime?.standard ? (
                  <span className="payment-view__price">{formatPrice(prices.lifetime.standard)}</span>
                ) : (
                  <span className="payment-view__price-hidden" />
                )}
              </div>
              <div className="payment-view__savings">One-time payment</div>
            </div>

            <ul className="payment-view__features-list payment-view__features-list--lifetime">
              {COPY.lifetime.features.map((feature, i) => (
                <li key={i} className="payment-view__feature-item">
                  <span className="payment-view__feature-check payment-view__feature-check--lifetime">
                    <Icon name="check" size={12} />
                  </span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button className="payment-view__cta-btn payment-view__cta-btn--lifetime">
              Get Lifetime Access
            </button>

            <div className="payment-view__guarantee">
              <Icon name="guarantee" size={16} />
              <span>14-day money-back guarantee</span>
            </div>
          </motion.div>
        </motion.div>

        <ValueComparison prices={prices} loading={loading} />
      </div>

      <FAQSection faqs={FAQ_DATA.lifetime} />
    </motion.div>
  )
}

// ============================================
// Main PaymentView Component
// ============================================

export default function PaymentView() {
  const [activeTab, setActiveTab] = useState('subscription')
  const { prices, loading } = usePrices()

  const renderStrategy = () => {
    switch (activeTab) {
      case 'subscription':
        return <SubscriptionStrategy key="subscription" prices={prices} loading={loading} />
      case 'project':
        return <ProjectStrategy key="project" prices={prices} loading={loading} />
      case 'lifetime':
        return <LifetimeStrategy key="lifetime" prices={prices} loading={loading} />
      default:
        return <SubscriptionStrategy key="subscription" prices={prices} loading={loading} />
    }
  }

  return (
    <motion.div
      className="payment-view"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Background Elements */}
      <div className="payment-view__bg-gradient" />
      <div className="payment-view__bg-grid" />

      {/* Header */}
      <header className="payment-view__header">
        <div className="payment-view__title-group">
          <div className="payment-view__icon">
            <Icon name="payments" size={28} />
          </div>
          <h1 className="payment-view__title">{COPY.page.title}</h1>
        </div>
        <p className="payment-view__subtitle">{COPY.page.subtitle}</p>
      </header>

      {/* Plan Selector (Toggle + Lifetime Tab) */}
      <PlanSelector activeTab={activeTab} onChange={setActiveTab} />

      {/* Strategy Content */}
      <AnimatePresence mode="wait">
        {renderStrategy()}
      </AnimatePresence>

      {/* Footer */}
      <footer className="payment-view__footer">
        <div className="payment-view__footer-content">
          <div className="payment-view__trust-badges">
            <span>
              <Icon name="secure" size={16} />
              {COPY.footer.secure}
            </span>
            <span>
              <Icon name="credits" size={16} />
              {COPY.footer.cards}
            </span>
            <span>
              <Icon name="globe" size={16} />
              {COPY.footer.global}
            </span>
          </div>
          <p className="payment-view__footer-note">
            {COPY.footer.note}
            <br />
            Need help? Contact us at support@{BRANDING.name.toLowerCase().replace(' ', '')}.app
          </p>
        </div>
      </footer>
    </motion.div>
  )
}
